import React, { useEffect, useState } from 'react';

const AddReservationModal = ({ newReservation, setNewReservation, onSubmit, users, workshops, statusLabels }) => {
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);

  useEffect(() => {
    if (newReservation.workshop) {
      const workshop = workshops.find(w => w.id === parseInt(newReservation.workshop));
      setSelectedWorkshop(workshop);

      // Get available dates for this workshop (dates not yet linked to a reservation)
      if (workshop && workshop.dates) {
        const available = workshop.dates.filter(date => !date.workshop_reservation || date.workshop_reservation === null);
        setAvailableDates(available);
      } else {
        setAvailableDates([]);
      }
    } else {
      setAvailableDates([]);
      setSelectedWorkshop(null);
    }
  }, [newReservation.workshop, workshops]);

  const handleUserChange = (userId) => {
    const user = users.find(u => u.id === parseInt(userId));
    setNewReservation({
      ...newReservation,
      user: userId,
      user_details: user ? {
        name: user.username || '',
        email: user.email || '',
        phone: user.phone || ''
      } : { name: '', email: '', phone: '' }
    });
  };

  const handleDateToggle = (dateId) => {
    const selected = [...newReservation.selectedDates];
    const index = selected.indexOf(dateId);

    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(dateId);
    }

    // Calculate total price
    const workshopPrice = selectedWorkshop?.price || 0;
    const totalPrice = selected.length * parseFloat(workshopPrice);

    setNewReservation({
      ...newReservation,
      selectedDates: selected,
      total_price: totalPrice
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="modal fade" id="addReservationModal" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Créer une réservation</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Utilisateur existant</label>
                <select
                  className="form-select"
                  value={newReservation.user}
                  onChange={(e) => handleUserChange(e.target.value)}
                >
                  <option value="">Aucun utilisateur (invité)</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.email})
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  Laissez vide pour créer une réservation sans compte utilisateur
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Atelier *</label>
                <select
                  className="form-select"
                  value={newReservation.workshop}
                  onChange={(e) => setNewReservation({
                    ...newReservation,
                    workshop: e.target.value,
                    selectedDates: [],
                    total_price: 0
                  })}
                  required
                >
                  <option value="">Sélectionner un atelier</option>
                  {workshops.map(workshop => (
                    <option key={workshop.id} value={workshop.id}>
                      {workshop.title} - {workshop.price && workshop.price > 0 ? `${workshop.price} €` : 'Gratuit'}
                    </option>
                  ))}
                </select>
              </div>

              {availableDates.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Dates disponibles *</label>
                  <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {availableDates.map(date => (
                      <div key={date.id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`date-${date.id}`}
                          checked={newReservation.selectedDates.includes(date.documentId)}
                          onChange={() => handleDateToggle(date.documentId)}
                        />
                        <label className="form-check-label" htmlFor={`date-${date.id}`}>
                          {formatDate(date.date)}
                          {date.is_full && <span className="badge bg-warning ms-2">Complet</span>}
                          <small className="text-muted ms-2">
                            ({date.current_participants || 0}/{date.max_participants || selectedWorkshop?.max_participants || 0} participants)
                          </small>
                        </label>
                      </div>
                    ))}
                  </div>
                  <small className="text-muted">
                    Sélectionné(s) : {newReservation.selectedDates.length} date(s)
                  </small>
                </div>
              )}

              {newReservation.workshop && availableDates.length === 0 && (
                <div className="alert alert-warning">
                  Aucune date disponible pour cet atelier
                </div>
              )}

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Statut</label>
                  <select
                    className="form-select"
                    value={newReservation.resa_status}
                    onChange={(e) => setNewReservation({ ...newReservation, resa_status: e.target.value })}
                  >
                    {Object.keys(statusLabels).map(status => (
                      <option key={status} value={status}>
                        {statusLabels[status]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Prix total</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={newReservation.total_price}
                    onChange={(e) => setNewReservation({ ...newReservation, total_price: e.target.value })}
                  />
                </div>
              </div>

              <hr />

              <h6>Détails utilisateur {!newReservation.user && <span className="text-danger">*</span>}</h6>
              {!newReservation.user && (
                <div className="alert alert-info">
                  <small>Ces informations sont obligatoires pour les réservations sans compte utilisateur</small>
                </div>
              )}
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Nom {!newReservation.user && '*'}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newReservation.user_details.name}
                    onChange={(e) => setNewReservation({
                      ...newReservation,
                      user_details: { ...newReservation.user_details, name: e.target.value }
                    })}
                    required={!newReservation.user}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Email {!newReservation.user && '*'}</label>
                  <input
                    type="email"
                    className="form-control"
                    value={newReservation.user_details.email}
                    onChange={(e) => setNewReservation({
                      ...newReservation,
                      user_details: { ...newReservation.user_details, email: e.target.value }
                    })}
                    required={!newReservation.user}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Téléphone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={newReservation.user_details.phone}
                    onChange={(e) => setNewReservation({
                      ...newReservation,
                      user_details: { ...newReservation.user_details, phone: e.target.value }
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Annuler
              </button>
              <button type="submit" className="btn btn-primary">
                Créer la réservation
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddReservationModal;
